import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { WebsiteImage } from 'components/TitleDescription';
import Cards from 'components/Core/Cards';
import CardsGroup from 'components/Core/Cards/CardsGroup';
import Button from 'components/Core/Button';

const PartnersFeatureSection = ({
  background,
  subTitle,
  title,
  description,
  sideImage,
  features,
  buttonTitle,
  buttonType,
  reversed,
  link,
  utm,
  ...otherProps
}) => {
  const [search, setSearch] = useState(undefined);

  useEffect(() => {
    setSearch(window?.location?.search);
  }, []);

  return (
    <SectionContainer background={background} reversed={reversed} {...otherProps}>
      <div className="section-partners-feature--container">
        <div className="section-partners-feature--main">
          <div className="section-partners-feature--text-side">
            <p className="section-partners-feature--subtitle">{subTitle}</p>
            <h2 className="section-partners-feature--title">{title}</h2>
            <p className="section-partners-feature--description">{description}</p>
          </div>
          <div className="section-partners-feature--image-side">
            <WebsiteImage src={sideImage} alt={title} />
          </div>
        </div>

        <div className="section-partners-feature--feature-container">
          {features?.length > 0 && (
            <CardsGroup cols={3}>
              {features.map((item, index) => (
                <Cards
                  type={'Icon'}
                  noShadow={true}
                  bodyBackColor={'transparent'}
                  align={'center'}
                  iconType={item.iconType}
                  title={item.title}
                  description={item.description}
                  key={index}
                />
              ))}
            </CardsGroup>
          )}

          <Button
            dataPosition="Partner Body - CTA"
            className="section-partners-feature--button"
            to={`${link}${utm && search ? search : ''}`}
          >
            {buttonTitle}
          </Button>
        </div>
      </div>
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  background: ${props => props.background};

  .section-partners-feature--container {
    padding: 99px 28px 124px;

    .section-partners-feature--main {
      display: flex;
      flex-direction: ${props => (props.reversed ? 'row-reverse' : 'row')};
      max-width: 1300px;
      margin: 0 auto;
      align-items: center;

      .section-partners-feature--text-side {
        flex: 6;
        padding-right: ${props => (props.reversed ? '0' : '93px')};
        padding-left: ${props => (!props.reversed ? '0' : '93px')};

        .section-partners-feature--subtitle {
          font-weight: 900;
          font-size: 16px;
          line-height: 20px;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--input-selected-color);
        }

        .section-partners-feature--title {
          font-weight: 900;
          font-size: 49px;
          line-height: 110%;
          text-align: left;
          letter-spacing: -0.04em;
          color: var(--normal-title-color);
          margin-top: 10px;
          max-width: 475px;
        }

        .section-partners-feature--description {
          font-weight: 500;
          font-size: 20px;
          line-height: 150%;
          color: var(--normal-description-color);
          margin-top: 40px;
          max-width: 475px;
        }
      }

      .section-partners-feature--image-side {
        flex: 8;

        img {
          width: 100%;
          max-width: 100%;
          height: auto;
        }
      }
    }

    .section-partners-feature--feature-container {
      max-width: 1250px;
      padding: 0;
      margin: 80px auto 0;
      text-align: center;

      .section-partners-feature--item-column {
        display: flex;
        width: 100%;

        .section-partners-feature--item {
          padding: 0 21px;
          margin-bottom: 48px;
          flex: 1;

          .ant-card {
            background: transparent;
            box-shadow: none !important;

            .ant-card-body {
              .industry-card--container {
                text-align: center;

                .industry-card--icon-container {
                  .st-icon-wrapper {
                    margin: 0 auto;

                    img {
                      width: 80px;
                      height: 80px;
                    }
                  }
                }

                .industry-card--content-container {
                  text-align: center;
                }
              }
            }
          }
        }
      }

      .section-partners-feature--button {
        padding: 20px 24px;
      }
    }
  }

  @media (max-width: 991px) {
    .section-partners-feature--container {
      padding: 64px 28px;

      .section-partners-feature--main {
        .section-partners-feature--text-side {
          .section-partners-feature--subtitle {
            font-size: 14px;
          }

          .section-partners-feature--title {
            font-size: 29px;
            letter-spacing: -0.03em;
          }

          .section-partners-feature--description {
            font-size: 17px;
            margin-top: 16px;
          }
        }

        .section-partners-feature--image-side {
          padding-left: ${props => (props.reversed ? '0' : '20px')};
          padding-right: ${props => (!props.reversed ? '0' : '20px')};
        }
      }

      .section-partners-feature--feature-container {
        margin-top: 40px;

        .section-partners-feature--item-column {
          .section-partners-feature--item {
            padding: 0;
            margin-bottom: 10px;
          }
        }
        .section-partners-feature--button {
          font-size: 16px;
          padding: 15px 24px;
        }
      }
    }
  }

  @media (max-width: 768px) {
  }

  @media (max-width: 576px) {
    .section-partners-feature--container {
      padding: 64px 20px;

      .section-partners-feature--main {
        flex-direction: column;

        .section-partners-feature--image-side {
          padding: 0;
          margin-top: 40px;
        }
      }

      .section-partners-feature--feature-container {
        .section-partners-feature--item-column {
          flex-direction: column;

          .section-partners-feature--item {
            .ant-card {
              .ant-card-body {
                padding: 0;

                .industry-card--container {
                  display: flex;
                  text-align: left;

                  .industry-card--content-container {
                    padding-left: 20px;
                    padding-top: 20px;
                    text-align: left;

                    .industry-card--description {
                      margin: 8px 0 0;
                      padding: 0;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default PartnersFeatureSection;
